// openEHR AM model — BMM package org.openehr.am.aom2.persistence.archetype
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { P_C_COMPLEX_OBJECT } from "./constraint_model.ts";
import type { P_ARCHETYPE_TERMINOLOGY } from "./terminology.ts";
import * as openehr_base from "../../../base/mod.ts";

/**
 * Archetype equivalent to ARCHETYPED class in Common reference model. Defines semantics of identfication, lifecycle, versioning, composition and specialisation.
 *
 * An archetype is a modelled as a particular kind of AUTHORED_RESOURCE, and as such, includes descriptive meta-data, language information and revision history. The ARCHETYPE class adds identifying information, a definition - expressed in terms of constraints on instances of an object model, and an ontology.
 */
export abstract class P_ARCHETYPE {
  /**
   * Internal storage for parent_archetype_id
   * @protected
   */
  protected _parent_archetype_id?: openehr_base.String;

  /**
   * Identifier of the specialisation parent of this archetype.
   */
  get parent_archetype_id(): string | undefined {
    return this._parent_archetype_id?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for parent_archetype_id.
   * Use this to access openehr_base.String methods.
   */
  get $parent_archetype_id(): openehr_base.String | undefined {
    return this._parent_archetype_id;
  }

  /**
   * Sets parent_archetype_id from either a primitive value or openehr_base.String wrapper.
   */
  set parent_archetype_id(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._parent_archetype_id = undefined;
    } else if (typeof val === "string") {
      this._parent_archetype_id = openehr_base.String.from(val);
    } else {
      this._parent_archetype_id = val;
    }
  }

  /**
   * Identifier of this archetype.
   */
  archetype_id?: P_ARCHETYPE_HRID;
  /**
   * Internal storage for is_differential
   * @protected
   */
  protected _is_differential?: openehr_base.Boolean;

  /**
   * Flag indicating whether this archetype is differential or flat in its contents. Top-level source archetypes have this flag set to True.
   */
  get is_differential(): boolean | undefined {
    return this._is_differential?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_differential.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_differential(): openehr_base.Boolean | undefined {
    return this._is_differential;
  }

  /**
   * Sets is_differential from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_differential(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_differential = undefined;
    } else if (typeof val === "boolean") {
      this._is_differential = openehr_base.Boolean.from(val);
    } else {
      this._is_differential = val;
    }
  }

  /**
   * Root node of the definition of this archetype.
   */
  definition?: P_C_COMPLEX_OBJECT;
  /**
   * The terminology of the archetype.
   */
  terminology?: P_ARCHETYPE_TERMINOLOGY;
  /**
   * Rules relating to this archetype. Statements are expressed in first order predicate logic, and usually refer to at least two attributes.
   */
  rules?: undefined;
}

/**
 * Abstract idea of an online resource created by a human author.
 */
export abstract class P_AUTHORED_RESOURCE {
  /**
   * Internal storage for original_language
   * @protected
   */
  protected _original_language?: openehr_base.String;

  /**
   * Language in which this resource was initially authored. Although there is no language primacy of resources overall, the language of original authoring is required to ensure natural language translations can preserve quality. Language is relevant in both the description and ontology sections.
   */
  get original_language(): string | undefined {
    return this._original_language?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for original_language.
   * Use this to access openehr_base.String methods.
   */
  get $original_language(): openehr_base.String | undefined {
    return this._original_language;
  }

  /**
   * Sets original_language from either a primitive value or openehr_base.String wrapper.
   */
  set original_language(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._original_language = undefined;
    } else if (typeof val === "string") {
      this._original_language = openehr_base.String.from(val);
    } else {
      this._original_language = val;
    }
  }

  /**
   * Internal storage for is_controlled
   * @protected
   */
  protected _is_controlled?: openehr_base.Boolean;

  /**
   * True if this resource is under any kind of change control (even file copying), in which case revision history is created.
   */
  get is_controlled(): boolean | undefined {
    return this._is_controlled?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_controlled.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_controlled(): openehr_base.Boolean | undefined {
    return this._is_controlled;
  }

  /**
   * Sets is_controlled from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_controlled(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_controlled = undefined;
    } else if (typeof val === "boolean") {
      this._is_controlled = openehr_base.Boolean.from(val);
    } else {
      this._is_controlled = val;
    }
  }

  /**
   * List of details for each natural translation made of this resource, keyed by language. For each translation listed here, there must be corresponding sections in all language-dependent parts of the resource. The original_language does not appear in this list.
   */
  translations?: undefined;
  /**
   * Description and lifecycle information of the resource.
   */
  description?: openehr_base.RESOURCE_DESCRIPTION;
  /**
   * Internal storage for uid
   * @protected
   */
  protected _uid?: openehr_base.String;

  /**
   * Unique identifier of the family of archetypes having the same interface identifier (same major version).
   */
  get uid(): string | undefined {
    return this._uid?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for uid.
   * Use this to access openehr_base.String methods.
   */
  get $uid(): openehr_base.String | undefined {
    return this._uid;
  }

  /**
   * Sets uid from either a primitive value or openehr_base.String wrapper.
   */
  set uid(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._uid = undefined;
    } else if (typeof val === "string") {
      this._uid = openehr_base.String.from(val);
    } else {
      this._uid = val;
    }
  }

  /**
   * Annotations on individual items within the resource, keyed by path. The inner table takes the form of a Hash table of String values keyed by String tags.
   */
  annotations?: undefined;
}

/**
 * Root object of a standalone, authored archetype, including all meta-data, description, other identifiers and lifecycle.
 */
export class P_AUTHORED_ARCHETYPE extends P_ARCHETYPE {
  /**
   * Internal storage for adl_version
   * @protected
   */
  protected _adl_version?: openehr_base.String;

  /**
   * ADL version if archteype was read in from an ADL sharable archetype.
   */
  get adl_version(): string | undefined {
    return this._adl_version?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for adl_version.
   * Use this to access openehr_base.String methods.
   */
  get $adl_version(): openehr_base.String | undefined {
    return this._adl_version;
  }

  /**
   * Sets adl_version from either a primitive value or openehr_base.String wrapper.
   */
  set adl_version(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._adl_version = undefined;
    } else if (typeof val === "string") {
      this._adl_version = openehr_base.String.from(val);
    } else {
      this._adl_version = val;
    }
  }

  /**
   * Unique identifier of this archetype artefact instance. A new identifier is assigned every time the content is changed by a tool. Used by tools to distinguish different revisions and/or interim snapshots of the same artefact.
   */
  build_uid?: openehr_base.UID;
  /**
   * Internal storage for rm_release
   * @protected
   */
  protected _rm_release?: openehr_base.String;

  /**
   * Semver.org compatible release of the most recent reference model release on which the archetype in its current version is based. This does not imply conformance only to this release, since an archetype may be valid with respect to multiple releases of a reference model.
   */
  get rm_release(): string | undefined {
    return this._rm_release?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for rm_release.
   * Use this to access openehr_base.String methods.
   */
  get $rm_release(): openehr_base.String | undefined {
    return this._rm_release;
  }

  /**
   * Sets rm_release from either a primitive value or openehr_base.String wrapper.
   */
  set rm_release(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._rm_release = undefined;
    } else if (typeof val === "string") {
      this._rm_release = openehr_base.String.from(val);
    } else {
      this._rm_release = val;
    }
  }

  /**
   * Internal storage for is_generated
   * @protected
   */
  protected _is_generated?: openehr_base.Boolean;

  /**
   * If True, indicates that this artefact was machine-generated from some other source, in which case, tools would expect to overwrite this artefact on a new generation. Editing tools should set this value to False when a user starts to manually edit an archetype.
   */
  get is_generated(): boolean | undefined {
    return this._is_generated?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_generated.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_generated(): openehr_base.Boolean | undefined {
    return this._is_generated;
  }

  /**
   * Sets is_generated from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_generated(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_generated = undefined;
    } else if (typeof val === "boolean") {
      this._is_generated = openehr_base.Boolean.from(val);
    } else {
      this._is_generated = val;
    }
  }

  other_meta_data?: undefined;
}

/**
 * Class representing source template, i.e. a kind of archetype that may include template overlays, and may be restricted by tools to only defining mandations, prohibitions, and restrictions on elements already defined in the flat parent.
 */
export class P_TEMPLATE extends P_AUTHORED_ARCHETYPE {
}

/**
 * Root object of an operational template. An operational template is derived from a TEMPLATE definition and the ARCHETYPEs and/or TEMPLATE_OVERLAYs mentioned by that template by a process of flattening, and potentially removal of unneeded languages and terminologies.
 *
 * An operational template is used for generating and validating canonical openEHR data, and also as a source artefact for generating other downstream technical artefacts, including XML schemas, APIs and UI form definitions.
 */
export class P_OPERATIONAL_TEMPLATE extends P_AUTHORED_ARCHETYPE {
  /**
   * Compendium of flattened terminologies of archetypes externally referenced from this archetype, keyed by archetype identifier. This will almost always be present in a template.
   */
  component_terminologies?: undefined;
  /**
   * Directory of term definitions as a two-level  table. The outer hash keys are term codes,  e.g. "at4", and the inner hash key are term  attribute names, e.g. "text", "description" etc.
   */
  terminology_extracts?: undefined;
}

/**
 * Human_readable identifier (HRID) for an archetype or template.
 */
export class P_ARCHETYPE_HRID {
  /**
   * Internal storage for namespace
   * @protected
   */
  protected _namespace?: openehr_base.String;

  /**
   * Reverse domain name namespace identifier.
   */
  get namespace(): string | undefined {
    return this._namespace?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for namespace.
   * Use this to access openehr_base.String methods.
   */
  get $namespace(): openehr_base.String | undefined {
    return this._namespace;
  }

  /**
   * Sets namespace from either a primitive value or openehr_base.String wrapper.
   */
  set namespace(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._namespace = undefined;
    } else if (typeof val === "string") {
      this._namespace = openehr_base.String.from(val);
    } else {
      this._namespace = val;
    }
  }

  /**
   * Internal storage for rm_publisher
   * @protected
   */
  protected _rm_publisher?: openehr_base.String;

  /**
   * Name of the Reference Model publisher.
   */
  get rm_publisher(): string | undefined {
    return this._rm_publisher?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for rm_publisher.
   * Use this to access openehr_base.String methods.
   */
  get $rm_publisher(): openehr_base.String | undefined {
    return this._rm_publisher;
  }

  /**
   * Sets rm_publisher from either a primitive value or openehr_base.String wrapper.
   */
  set rm_publisher(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._rm_publisher = undefined;
    } else if (typeof val === "string") {
      this._rm_publisher = openehr_base.String.from(val);
    } else {
      this._rm_publisher = val;
    }
  }

  /**
   * Internal storage for rm_package
   * @protected
   */
  protected _rm_package?: openehr_base.String;

  /**
   * Name of the package in whose reachability graph the rm_class class is found (there can be more than one possibility in many reference models).
   */
  get rm_package(): string | undefined {
    return this._rm_package?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for rm_package.
   * Use this to access openehr_base.String methods.
   */
  get $rm_package(): openehr_base.String | undefined {
    return this._rm_package;
  }

  /**
   * Sets rm_package from either a primitive value or openehr_base.String wrapper.
   */
  set rm_package(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._rm_package = undefined;
    } else if (typeof val === "string") {
      this._rm_package = openehr_base.String.from(val);
    } else {
      this._rm_package = val;
    }
  }

  /**
   * Internal storage for rm_class
   * @protected
   */
  protected _rm_class?: openehr_base.String;

  /**
   * Name of the root class of this archetype.
   */
  get rm_class(): string | undefined {
    return this._rm_class?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for rm_class.
   * Use this to access openehr_base.String methods.
   */
  get $rm_class(): openehr_base.String | undefined {
    return this._rm_class;
  }

  /**
   * Sets rm_class from either a primitive value or openehr_base.String wrapper.
   */
  set rm_class(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._rm_class = undefined;
    } else if (typeof val === "string") {
      this._rm_class = openehr_base.String.from(val);
    } else {
      this._rm_class = val;
    }
  }

  /**
   * Internal storage for concept_id
   * @protected
   */
  protected _concept_id?: openehr_base.String;

  /**
   * The short concept name of the archetype as used in the multi-axial archetype_hrid.
   */
  get concept_id(): string | undefined {
    return this._concept_id?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for concept_id.
   * Use this to access openehr_base.String methods.
   */
  get $concept_id(): openehr_base.String | undefined {
    return this._concept_id;
  }

  /**
   * Sets concept_id from either a primitive value or openehr_base.String wrapper.
   */
  set concept_id(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._concept_id = undefined;
    } else if (typeof val === "string") {
      this._concept_id = openehr_base.String.from(val);
    } else {
      this._concept_id = val;
    }
  }

  /**
   * Internal storage for release_version
   * @protected
   */
  protected _release_version?: openehr_base.String;

  /**
   * The full numeric version of this archetype consisting of 3 parts, e.g. 1.8.2. The archetype_hrid feature includes only the major version.
   */
  get release_version(): string | undefined {
    return this._release_version?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for release_version.
   * Use this to access openehr_base.String methods.
   */
  get $release_version(): openehr_base.String | undefined {
    return this._release_version;
  }

  /**
   * Sets release_version from either a primitive value or openehr_base.String wrapper.
   */
  set release_version(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._release_version = undefined;
    } else if (typeof val === "string") {
      this._release_version = openehr_base.String.from(val);
    } else {
      this._release_version = val;
    }
  }

  /**
   * The status of the version, i.e. released, release_candidate etc.
   */
  version_status?: openehr_base.VERSION_STATUS;
  /**
   * Internal storage for build_count
   * @protected
   */
  protected _build_count?: openehr_base.String;

  /**
   * The build count since last increment of any version part.
   */
  get build_count(): string | undefined {
    return this._build_count?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for build_count.
   * Use this to access openehr_base.String methods.
   */
  get $build_count(): openehr_base.String | undefined {
    return this._build_count;
  }

  /**
   * Sets build_count from either a primitive value or openehr_base.String wrapper.
   */
  set build_count(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._build_count = undefined;
    } else if (typeof val === "string") {
      this._build_count = openehr_base.String.from(val);
    } else {
      this._build_count = val;
    }
  }
}
